import {
  BestAvailableTransfersOrder,
  PositionType,
  rdbV1FiltersPositionsList,
  type RdbV1FiltersPositionsListParams,
  rdbV1FiltersTeamsList,
  type RdbV1FiltersTeamsListParams,
  rdbV1FiltersYearsList,
  rdbV1TransfersBestAvailableList,
  type RdbV1TransfersBestAvailableListParams,
  rdbV1TransfersLatestList,
  type RdbV1TransfersLatestListParams,
} from '@on3/api';
import type {
  IGenericPageProps,
  TransferPortalPageProps,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import {
  getFulfilledValue,
  proxyApi,
  proxyApiWithVary,
} from '@on3/ui-lib/utils/api';
import { capitalizeFirstLetter } from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TransferPortalPage } from '@public/components/Db/TransferPortal/TransferPortalPage';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import {
  createCanonicalUrl,
  createDescription,
  createTitle,
} from '@public/utils/meta';
import { cleanSsrData } from '@public/utils/ssr';
import type { GetServerSideProps } from 'next';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

export const TransferType = {
  industry: 'best-available',
  top: 'best-available',
  wire: 'latest',
};
export const OrderByType = {
  wire: 'Default',
  top: 'Default',
  industry: 'Industry',
};

interface TransferPortalProps
  extends IGenericPageProps,
    TransferPortalPageProps {}

export const TransferPortal = ({
  playerData,
  filters,
  siteData,
  pageParams,
  ...props
}: TransferPortalProps) => {
  const router = useRouter();
  const { relatedModel, pagination } = playerData || {};
  const { position, type, sport, year } = relatedModel || {};
  const { status, orderby, transferType } = router.query;
  const isTop = type === 'best-available';
  const statusType = status?.toString();
  const isBest = statusType?.includes('best');
  const currentPosition = position?.name || null;
  const componentKey = `${sport?.name}-${
    currentPosition ?? 'all'
  }-${year}-${pagination?.currentPage}-${type}-${status?.toString()}-${orderby}-${transferType}`;

  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);
  const isCurrentYear = year === siteData?.defaultYears?.transferYear;

  const pageTitle = createTitle(
    isCurrentYear ? '' : year,
    'College',
    sport?.name,
    isBest ? 'Best Available' : '',
    isTop && !isBest ? 'Top' : '',
    'Transfer Portal',
    isTop ? `${currentPosition ?? ''} Players` : '',
  );

  const pageDescription = createDescription(
    `The {0} {1} college {2} {3} players that have entered the transfer portal.`,
    isTop ? 'best' : 'latest',
    year,
    sport?.name?.toLowerCase(),
    currentPosition ? `${currentPosition?.toLowerCase()}` : '',
  );

  const noIndex = pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={pageDescription}
      noIndex={noIndex}
      siteData={siteData}
      title={pageTitle}
    >
      <AdSidebarLayout>
        <TransferPortalPage
          filters={filters}
          key={componentKey}
          pageParams={pageParams}
          pageTitle={pageTitle}
          playerData={playerData}
        />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const transferPortalSiteKey = 369;
  const siteData = await withValidUrl.isValid(ctx, transferPortalSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const { requestWithVaryParams } = userData || {};
  const {
    status,
    transferType,
    year,
    orderby,
    position,
    sport,
    page = '1',
  } = ctx.query as {
    status?: string;
    transferType: string;
    year?: string;
    orderby?: string;
    position?: string;
    sport: string;
    page?: string;
  };

  const capitalizeStatus = status ? capitalizeFirstLetter(`${status}`) : null;
  const { rankingSports, defaultYears } = siteData || {};

  const currentYear = new Date().getFullYear();
  const yearNumber = year && parseInt(year, 10);
  const selectedYear = yearNumber || defaultYears?.transferYear;
  const sportObj = rankingSports?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  );
  const sportKey = sportObj?.key;

  let updatedUrl = ctx.resolvedUrl;
  let shouldRedirect = false;

  if (
    !selectedYear ||
    isNaN(selectedYear) ||
    selectedYear < 2020 ||
    selectedYear > currentYear + 1 ||
    !sportKey
  ) {
    return {
      notFound: true,
    };
  }

  // 1. Remove the year from the URL if it's the default/current transfer year
  if (yearNumber === defaultYears?.transferYear) {
    updatedUrl = updatedUrl.replace(`/${yearNumber}`, '');
    shouldRedirect = true;
  }

  // 2. Replace "industry" with "top" if the sport is not industry-rankable
  if (transferType === 'industry' && !sportObj?.isIndustryRankable) {
    updatedUrl = updatedUrl.replace('/industry/', '/top/');
    shouldRedirect = true;
  }

  // Final redirect if any changes were made
  if (shouldRedirect) {
    return {
      redirect: {
        destination: updatedUrl,
        permanent: false,
      },
    };
  }

  try {
    const positionParams: RdbV1FiltersPositionsListParams = {
      positionType: PositionType.Player,
      sportKey,
    };
    const teamParams: RdbV1FiltersTeamsListParams = {
      sportKey,
      groupBy: true,
      year: selectedYear,
    };

    const baseParams: RdbV1TransfersLatestListParams = {
      sportKey,
      year: selectedYear,
      status: capitalizeStatus?.includes('Best')
        ? 'Entered'
        : capitalizeStatus?.toString(),
      positionAbbr: position?.toString(),
      page: +page,
    };

    const bestParams: RdbV1TransfersBestAvailableListParams = {
      ...baseParams,
      orderBy: (() => {
        if (orderby === 'nilvalue') {
          return BestAvailableTransfersOrder.NilValue;
        }

        if (transferType === 'industry') {
          return BestAvailableTransfersOrder.Industry;
        }

        return BestAvailableTransfersOrder.Default;
      })(),
    };

    const promises: Promise<any>[] = [
      rdbV1FiltersYearsList(proxyApi),
      rdbV1FiltersTeamsList(proxyApi, teamParams),
      rdbV1FiltersPositionsList(proxyApi, positionParams),
    ];

    if (transferType === 'wire') {
      promises.push(
        rdbV1TransfersLatestList(
          proxyApiWithVary,
          baseParams,
          requestWithVaryParams,
        ),
      );
    } else {
      promises.push(
        rdbV1TransfersBestAvailableList(
          proxyApiWithVary,
          bestParams,
          requestWithVaryParams,
        ),
      );
    }

    const [years, teams, positions, playerData] =
      await Promise.allSettled(promises);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || null,
      playerData: getFulfilledValue(playerData),
      filters: {
        years: getFulfilledValue(years),
        sports: rankingSports,
        teams: getFulfilledValue(teams),
        positions: getFulfilledValue(positions),
      },
      pageParams: {
        baseParams: cleanSsrData(baseParams),
        bestParams: cleanSsrData(bestParams),
      },
    };

    return { props: { ...data } };
  } catch (e) {
    return {
      props: {
        playerData: [],
        filters: {
          years: [],
          sports: [],
          teams: [],
          orgTypes: [],
          states: [],
          positions: [],
          conferences: [],
        },
        siteData,
        userData: userData || {},
        pageParams: {
          baseParams: {},
          bestParams: {},
        },
      },
    };
  }
};

export default withValidUrl(TransferPortal);
